import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { I18nProvider } from "./i18n";
import { AuthProvider, useAuth } from "./context/AuthContext";
import DemoBanner from "./components/app/DemoBanner";

import Home from "./pages/public/Home";
import Pricing from "./pages/public/Pricing";
import Diagnostic from "./pages/public/Diagnostic";
import Marketing from "./pages/public/Marketing";
import TaxServices from "./pages/public/TaxServices";
import TaxDetail from "./pages/public/TaxDetail";
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import AuthCallback from "./pages/auth/AuthCallback";

import AppLayout from "./components/app/AppLayout";
import DashboardRouter from "./pages/app/DashboardRouter";import Onboarding from "./pages/app/Onboarding";
import CasesList from "./pages/app/CasesList";
import CaseDetail from "./pages/app/CaseDetail";
import NewRequest from "./pages/app/NewRequest";
import DocumentsRoom from "./pages/app/DocumentsRoom";
import Subscription from "./pages/app/Subscription";
import Notifications from "./pages/app/Notifications";
import Timeline from "./pages/app/Timeline";
import CaseReport from "./pages/app/CaseReport";
import AIAssistant from "./pages/app/AIAssistant";
import ManagementDashboard from "./pages/app/ManagementDashboard";
import Workload from "./pages/app/Workload";
import UsersAdmin from "./pages/app/UsersAdmin";
import ClientsList from "./pages/app/ClientsList";
import TimeReport from "./pages/app/TimeReport";
import EngagementSummary from "./pages/app/EngagementSummary";
import Knowledge from "./pages/app/Knowledge";
import AIToolkit from "./pages/app/AIToolkit";
import VatControl from "./pages/app/VatControl";
import RulesAdmin from "./pages/app/RulesAdmin";
import Structure from "./pages/app/Structure";
import ClientPortal from "./pages/app/ClientPortal";
import KnowledgeSources from "./pages/app/KnowledgeSources";
import AIWorkspace from "./pages/app/AIWorkspace";
import Leads from "./pages/app/Leads";
import ChangePassword from "./pages/app/ChangePassword";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center gc-gradient-navy text-white">
        <div className="text-center">
          <div className="h-10 w-10 rounded-full border-2 border-white/30 border-t-white animate-spin mx-auto" />
          <div className="mt-4 text-sm text-white/70">VATRdar</div>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

const DEMO = process.env.REACT_APP_DEMO_MODE === "true";
function DemoHidden({ children }) {
  return DEMO ? <Navigate to="/app" replace /> : children;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/tax-services" element={<TaxServices />} />
      <Route path="/tax/:slug" element={<TaxDetail />} />
      <Route path="/how-it-works" element={<Marketing page="how" />} />
      <Route path="/vat-health-check" element={<Marketing page="health" />} />
      <Route path="/vat-compliance" element={<Marketing page="compliance" />} />
      <Route path="/vat-advisory" element={<Marketing page="advisory" />} />
      <Route path="/zatca-audit" element={<Marketing page="zatca" />} />
      <Route path="/knowledge" element={<Marketing page="knowledge" />} />
      <Route path="/about" element={<Marketing page="about" />} />
      <Route path="/contact" element={<Marketing page="contact" />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/diagnostic" element={<Diagnostic />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />

      <Route path="/app/cases/:id/report" element={<Protected><CaseReport /></Protected>} />

      <Route path="/app" element={<Protected><AppLayout /></Protected>}>
        <Route index element={<DashboardRouter />} />
        <Route path="onboarding" element={<Onboarding />} />
        <Route path="cases" element={<CasesList />} />
        <Route path="cases/:id" element={<CaseDetail />} />
        <Route path="timeline" element={<Timeline />} />
        <Route path="new" element={<NewRequest />} />
        <Route path="documents" element={<DocumentsRoom />} />
        <Route path="subscription" element={<Subscription />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="change-password" element={<ChangePassword />} />
        <Route path="assistant" element={<DemoHidden><AIAssistant /></DemoHidden>} />
        <Route path="queue" element={<CasesList />} />
        <Route path="management" element={<ManagementDashboard />} />
        <Route path="workload" element={<Workload />} />
        <Route path="users" element={<UsersAdmin />} />
        <Route path="leads" element={<Leads />} />
        <Route path="clients" element={<ClientsList />} />
        <Route path="clients/:orgId" element={<EngagementSummary />} />
        <Route path="summary" element={<EngagementSummary />} />
        <Route path="time-report" element={<TimeReport />} />
        <Route path="knowledge" element={<DemoHidden><Knowledge /></DemoHidden>} />
        <Route path="toolkit" element={<DemoHidden><AIToolkit /></DemoHidden>} />
        <Route path="control" element={<VatControl />} />
        <Route path="rules" element={<RulesAdmin />} />
        <Route path="structure" element={<Structure />} />
        <Route path="client" element={<ClientPortal />} />
        <Route path="knowledge-sources" element={<DemoHidden><KnowledgeSources /></DemoHidden>} />
        <Route path="ai-workspace/*" element={<DemoHidden><AIWorkspace /></DemoHidden>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          {DEMO && <DemoBanner />}
          <AppRoutes />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
